import { useEffect, useRef, type MouseEvent, type ReactNode, type SyntheticEvent } from 'react';

import { usePresence, useSheetDrag } from '../../lib/motion/hooks';

type DrawerProps = {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
};

/** How long the drawer takes to leave; it stays mounted until then. */
const EXIT_MS = 220;

/**
 * The panel's one modal surface: a native `<dialog>` styled as a right-side
 * sheet (a bottom sheet on the phone), so every create form lives behind a
 * button instead of permanently open under its list. It slides in from its
 * edge and back out, keeping its last content (inert, hidden from assistive
 * tech: it is already closed, only still moving) while it leaves; Esc,
 * backdrop-click and a downward flick on the phone all land on `onClose`.
 * Focus trapping and inerting the page come free with `showModal()`.
 */
export function Drawer({ open, title, onClose, children }: DrawerProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const { mounted, phase } = usePresence(open, { exitMs: EXIT_MS });
  const lastChildren = useRef(children);
  if (open) lastChildren.current = children;
  const drag = useSheetDrag(ref, onClose);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (phase === 'opening' && !dialog.open) dialog.showModal();
    else if (phase === 'closed' && dialog.open) dialog.close();
  }, [phase]);

  function handleBackdropClick(event: MouseEvent<HTMLDialogElement>) {
    if (event.target === ref.current) onClose();
  }

  // Esc leaves the same way the Cerrar button does, sliding out instead of vanishing.
  function handleCancel(event: SyntheticEvent<HTMLDialogElement>) {
    event.preventDefault();
    onClose();
  }

  return (
    <dialog
      ref={ref}
      className="drawer"
      data-phase={phase}
      inert={phase === 'closing'}
      aria-hidden={phase === 'closing' || undefined}
      aria-label={title}
      onClose={onClose}
      onCancel={handleCancel}
      onClick={handleBackdropClick}
      {...drag}
    >
      {mounted ? (
        <div className="drawer-body">
          <span className="drawer-grab" aria-hidden="true" />
          <div className="drawer-head">
            <h2 className="drawer-title">{title}</h2>
            <button type="button" className="btn small ghost" onClick={onClose}>
              Cerrar
            </button>
          </div>
          {open ? children : lastChildren.current}
        </div>
      ) : null}
    </dialog>
  );
}
